// @ts-nocheck
import React, { useState } from 'react';
// import { Settings } from 'lucide-react'; // gear button commented out below
import { useTranscriptStore } from '@/router/zustand/transcriptStore';
import { useCallTranscription } from '@/features/cti2/hooks/useCallTranscription';
import {
  AGENT_SPEECH_LANGUAGES,
  CUSTOMER_TRANSCRIPTION_ENABLED,
  CUSTOMER_NATIVE_TRANSCRIPTION_ENABLED,
  CUSTOMER_NATIVE_MIN_CHROMIUM_VERSION,
} from '@/utilities/constant';
import { Panel, Pill, TONE, HAIRLINE } from '@/features/cti2/team/teamUi';
import TranscriptionSetupModal from './TranscriptionSetupModal';

const ERROR_HINTS = {
  'not-allowed': 'Microphone permission blocked — allow it in the browser address bar.',
  'audio-capture': 'No microphone found — check the input device.',
  network: 'Speech service unreachable — check the network connection.',
  'language-not-supported': 'Selected language pack is not installed for this browser.',
  'start-failed': 'Could not start speech recognition — try again.',
};

function describeError(error) {
  if (!error) return '';
  return ERROR_HINTS[error] || `Speech recognition error: ${error}`;
}

const SHOW_CUSTOMER = CUSTOMER_TRANSCRIPTION_ENABLED && CUSTOMER_NATIVE_TRANSCRIPTION_ENABLED;

function agentPlaceholder({ supported, status, error }) {
  if (!supported) return { text: 'Speech recognition is not supported in this browser.', isProblem: true };
  if (error) return { text: describeError(error), isProblem: true };
  if (status === 'stopped') return { text: 'Transcription stopped.', isProblem: true };
  if (status === 'running') return { text: 'Listening…', isProblem: false };
  return { text: 'Waiting for call…', isProblem: false };
}

function customerPlaceholder({ customerSupported, customerStatus }) {
  if (!customerSupported) return { text: 'Not supported in this browser.', isProblem: true };
  if (customerStatus === 'error') return { text: 'Customer transcription failed.', isProblem: true };
  if (customerStatus === 'unsupported') return { text: 'This browser could not isolate the customer audio.', isProblem: true };
  if (customerStatus === 'no-audio') return { text: 'No speech detected yet.', isProblem: false };
  if (customerStatus === 'paused') return { text: 'Paused (on hold).', isProblem: false };
  if (customerStatus === 'waiting') return { text: 'Waiting for customer audio…', isProblem: false };
  if (customerStatus === 'running') return { text: 'Listening…', isProblem: false };
  return { text: 'Waiting for call…', isProblem: false };
}

function renderPlaceholder(placeholder, onOpenSetup) {
  return (
    <div className="text-xs text-muted-foreground">
      <span className={placeholder.isProblem ? 'text-destructive' : ''}>{placeholder.text}</span>
      {placeholder.isProblem ? (
        <button
          type="button"
          onClick={onOpenSetup}
          className="ml-2 text-[11px] font-medium text-primary underline underline-offset-2"
        >
          Transcription setup
        </button>
      ) : null}
    </div>
  );
}

export default function LiveCallTranscript({ agentName = 'Agent' }) {
  useCallTranscription();

  const lines = useTranscriptStore((s) => s.lines);
  const interim = useTranscriptStore((s) => s.interim);
  const supported = useTranscriptStore((s) => s.supported);
  const status = useTranscriptStore((s) => s.status);
  const error = useTranscriptStore((s) => s.error);
  const agentLang = useTranscriptStore((s) => s.agentLang);
  const setAgentLang = useTranscriptStore((s) => s.setAgentLang);

  const customerInterim = useTranscriptStore((s) => s.customerInterim);
  const customerStatus = useTranscriptStore((s) => s.customerStatus);
  const customerLang = useTranscriptStore((s) => s.customerLang);
  const setCustomerLang = useTranscriptStore((s) => s.setCustomerLang);
  const customerSupported = useTranscriptStore((s) => s.customerSupported);

  const [showSetup, setShowSetup] = useState(false);

  const agentLines = lines.filter((l) => l.speaker !== 'Customer');
  const customerLines = lines.filter((l) => l.speaker === 'Customer');

  const agentPh = agentPlaceholder({ supported, status, error });
  const customerPh = customerPlaceholder({ customerSupported, customerStatus });

  return (
    <Panel className="flex shrink-0 flex-col gap-2 p-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Live Transcript</span>
          <Pill tone="info">Chrome with WebRTC only</Pill>
        </div>
        {/* Setup-help gear hidden; modal still opens via the "Transcription setup" link on errors.
        <button
          type="button"
          onClick={() => setShowSetup(true)}
          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          aria-label="Transcription setup"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
        */}
      </div>

      {supported ? (
        <select
          value={agentLang}
          onChange={(e) => setAgentLang(e.target.value)}
          className={`w-fit rounded-md border bg-background px-2 py-1 text-[11px] text-foreground ${HAIRLINE}`}
        >
          {AGENT_SPEECH_LANGUAGES.map((l) => (
            <option key={l.code} value={l.code}>{l.label}</option>
          ))}
        </select>
      ) : null}

      <div className={`max-h-40 min-h-[4rem] overflow-y-auto rounded-lg border bg-muted/20 px-2 py-1.5 ${HAIRLINE}`}>
        {interim ? <div className="text-xs italic text-muted-foreground">{interim}</div> : null}
        {agentLines.length > 0 ? (
          agentLines.map((l, i) => (
            <div key={i} className="text-xs text-foreground">
              <span className={TONE.call.text}>{agentName} (Agent):</span> {l.text}
            </div>
          ))
        ) : !interim ? (
          renderPlaceholder(agentPh, () => setShowSetup(true))
        ) : null}
      </div>

      {SHOW_CUSTOMER ? (
        <>
          <hr className={HAIRLINE} />
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-foreground">Customer Transcription</span>
            <Pill
              tone="info"
              title={`Requires Chrome ${CUSTOMER_NATIVE_MIN_CHROMIUM_VERSION}+ reading the incoming call audio directly; other browsers cannot isolate it from the microphone.`}
            >
              Chrome with WebRTC only
            </Pill>
          </div>

          <select
            value={customerLang}
            onChange={(e) => setCustomerLang(e.target.value)}
            className={`w-fit rounded-md border bg-background px-2 py-1 text-[11px] text-foreground ${HAIRLINE}`}
          >
            {AGENT_SPEECH_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>{l.label}</option>
            ))}
          </select>

          <div className={`max-h-40 min-h-[4rem] overflow-y-auto rounded-lg border bg-muted/20 px-2 py-1.5 ${HAIRLINE}`}>
            {customerInterim ? <div className="text-xs italic text-muted-foreground">{customerInterim}</div> : null}
            {customerLines.length > 0 ? (
              customerLines.map((l, i) => (
                <div key={i} className="text-xs text-foreground">
                  <span className={TONE.warn.text}>Customer:</span> {l.text}
                </div>
              ))
            ) : !customerInterim ? (
              renderPlaceholder(customerPh, () => setShowSetup(true))
            ) : null}
          </div>
        </>
      ) : null}

      <TranscriptionSetupModal open={showSetup} onOpenChange={setShowSetup} />
    </Panel>
  );
}
